import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { DELIVERY_METHOD_PICKUP } from '../../util/cart';
import { freeShippingProgress, PACKING_CLASS_DRY } from '../../util/freeShippingThreshold';

import { PrimaryButton } from '../../components';

import CartRow from './CartRow';
import {
  boxGrandTotalSubunits,
  liveItemsSubtotalSubunits,
  riderUnitCount,
} from './CartPage.shared';
import css from './CartBoxCard.module.css';

const { Money } = sdkTypes;

const money = (intl, amount, currency) =>
  amount != null && currency ? formatMoney(intl, new Money(amount, currency)) : null;

const firstNonEmptyText = (rows, key) => {
  for (const row of rows) {
    const text = typeof row?.[key] === 'string' ? row[key].trim() : '';
    if (text) {
      return text;
    }
  }
  return null;
};

const sellerInitial = name => {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

/**
 * Every live box shows Items, Shipping (or pickup), then Total. Combined shipping
 * tucks the highest-fee / rider math under Shipping as quieter sublines.
 */
const BoxTotals = ({ ledger, rows, intl, isPickup, packingClass }) => {
  const currency = ledger?.currency || rows.find(r => r.currency)?.currency;
  const items = liveItemsSubtotalSubunits(rows);
  const shipping = ledger?.total || 0;
  const grand = boxGrandTotalSubunits(rows, ledger);
  const riderUnits = riderUnitCount(ledger);
  const riderAmount = (ledger?.riders || []).reduce((sum, r) => sum + (r.amount || 0), 0);
  const savings = ledger?.savings || 0;
  const riderMoney = money(intl, riderAmount, currency);
  const showShippingMath = !isPickup && riderUnits > 0;
  const thresholdSubunits = ledger?.freeShippingThresholdSubunits;
  const merchandiseSubunits =
    typeof ledger?.merchandiseSubunits === 'number'
      ? ledger.merchandiseSubunits
      : liveItemsSubtotalSubunits(rows);
  const freeShippingOfferActive =
    !isPickup && typeof thresholdSubunits === 'number' && thresholdSubunits > 0;
  const freeShippingApplied = ledger?.freeShippingApplied === true;
  const freeShippingProgressState = freeShippingOfferActive
    ? freeShippingProgress({ merchandiseSubunits, thresholdSubunits })
    : null;
  const freeShippingRemainingMoney =
    freeShippingProgressState && !freeShippingProgressState.met
      ? money(intl, freeShippingProgressState.remainingSubunits, currency)
      : null;

  return (
    <div className={css.totals}>
      <dl className={css.ledger}>
        <div className={css.ledgerLine}>
          <dt className={css.ledgerLabel}>
            <FormattedMessage id="CartBoxCard.ledgerItems" />
          </dt>
          <dd className={css.ledgerAmount}>{money(intl, items, currency)}</dd>
        </div>
        {isPickup ? (
          <div className={css.ledgerLine}>
            <dt className={css.ledgerLabel}>
              <FormattedMessage id="CartBoxCard.pickupNoShipping" />
            </dt>
            <dd className={css.ledgerAmount}>{money(intl, 0, currency)}</dd>
          </div>
        ) : (
          <>
            <div className={css.ledgerLine}>
              <dt className={css.ledgerLabel}>
                <FormattedMessage id="CartBoxCard.ledgerShipping" />
              </dt>
              <dd className={css.ledgerAmount}>{money(intl, shipping, currency)}</dd>
            </div>
            {showShippingMath ? (
              <>
                <div className={css.ledgerSubLine}>
                  <dt>
                    <FormattedMessage id="CartBoxCard.ledgerHighestFee" />
                  </dt>
                  <dd>{money(intl, ledger.firstItemFee, currency)}</dd>
                </div>
                <div className={css.ledgerSubLine}>
                  <dt>
                    <FormattedMessage
                      id="CartBoxCard.ledgerRiders"
                      values={{ count: riderUnits }}
                    />
                  </dt>
                  <dd>
                    {riderAmount > 0 ? (
                      <FormattedMessage
                        id="CartBoxCard.ledgerRiderAmount"
                        values={{ amount: riderMoney }}
                      />
                    ) : (
                      riderMoney
                    )}
                  </dd>
                </div>
              </>
            ) : null}
          </>
        )}
        <div className={css.ledgerGrandLine}>
          <dt className={css.ledgerTotalLabel}>
            <FormattedMessage id="CartBoxCard.ledgerBoxTotal" />
          </dt>
          <dd className={css.ledgerTotalAmount}>{money(intl, grand, currency)}</dd>
        </div>
      </dl>
      {showShippingMath && savings > 0 ? (
        <p className={css.savings}>
          <FormattedMessage
            id="CartBoxCard.savings"
            values={{ amount: money(intl, savings, currency) }}
          />
        </p>
      ) : null}
      {freeShippingApplied ? (
        <p className={css.savings}>
          <FormattedMessage id="CartBoxCard.freeShippingUnlocked" />
        </p>
      ) : freeShippingRemainingMoney ? (
        <p className={css.savings}>
          <FormattedMessage
            id={
              packingClass === PACKING_CLASS_DRY
                ? 'CartBoxCard.freeShippingProgressDry'
                : 'CartBoxCard.freeShippingProgressLive'
            }
            values={{ remaining: freeShippingRemainingMoney }}
          />
        </p>
      ) : null}
    </div>
  );
};

/**
 * One box: one payment, one shipment, one conversation, one review.
 *
 * @component
 */
const CartBoxCard = props => {
  const {
    box,
    isGuest,
    signInHref,
    conflictedListingIds = [],
    updatingListingId = null,
    updateInProgress = false,
    onRemove,
    onUpdateQty,
    onUpdateDeliveryMethod,
    onCheckout,
    checkoutInProgress = false,
  } = props;
  const intl = useIntl();

  const {
    ledger,
    rows,
    liveRows,
    boxNumber,
    sellerBoxCount = 1,
    deliveryMethod,
    packingClass,
  } = box;
  const isPickup = deliveryMethod === DELIVERY_METHOD_PICKUP;
  const hasLiveRows = liveRows.length > 0;

  const sellerName = rows.find(r => r.sellerName)?.sellerName || null;
  const sellerAvatarUrl = rows.find(r => r.sellerAvatarUrl)?.sellerAvatarUrl || null;
  const doaPolicy =
    firstNonEmptyText(rows, 'sellerDoaReturnPolicy') ||
    firstNonEmptyText(rows, 'sellerShippingPolicy');
  const pillKind = isPickup ? 'pickup' : packingClass || 'other';
  const otherBoxNumber = sellerBoxCount === 2 && boxNumber ? (boxNumber === 1 ? 2 : 1) : null;

  const checkoutControl = isGuest ? (
    <a
      className={css.signInButton}
      href={signInHref}
      aria-disabled={hasLiveRows ? undefined : true}
    >
      <FormattedMessage id="CartBoxCard.signInToCheckOut" />
    </a>
  ) : (
    <PrimaryButton
      className={css.checkoutButton}
      type="button"
      disabled={!hasLiveRows || checkoutInProgress}
      inProgress={checkoutInProgress}
      onClick={() => onCheckout(box)}
    >
      <FormattedMessage id="CartBoxCard.checkoutThisBox" />
    </PrimaryButton>
  );

  return (
    <section className={css.root}>
      <header className={css.header}>
        <div className={css.seller}>
          <span className={css.avatar} aria-hidden="true">
            {sellerAvatarUrl ? (
              <img className={css.avatarImage} src={sellerAvatarUrl} alt="" />
            ) : (
              sellerInitial(sellerName)
            )}
          </span>
          <div className={css.sellerText}>
            <h2 className={css.sellerName}>
              {sellerName || <FormattedMessage id="CartBoxCard.unknownSeller" />}
            </h2>
            <p className={css.methodLine}>
              <FormattedMessage
                id={isPickup ? 'CartBoxCard.pickupTogether' : 'CartBoxCard.shipsTogether'}
              />
            </p>
          </div>
        </div>
        <p
          className={classNames(css.boxPill, {
            [css.boxPillLive]: pillKind === 'live',
          })}
        >
          {boxNumber ? (
            <FormattedMessage
              id="CartBoxCard.boxPill"
              values={{ number: boxNumber, packingClass: pillKind }}
            />
          ) : (
            <FormattedMessage id="CartBoxCard.packingPill" values={{ packingClass: pillKind }} />
          )}
        </p>
      </header>

      <ul className={css.rows}>
        {rows.map(row => (
          <CartRow
            key={row.listingId}
            row={row}
            ledger={ledger}
            isUpdating={updateInProgress && updatingListingId === row.listingId}
            disabled={updateInProgress && updatingListingId !== row.listingId}
            conflictedOnMerge={conflictedListingIds.includes(row.listingId)}
            onRemove={onRemove}
            onUpdateQty={onUpdateQty}
            onUpdateDeliveryMethod={onUpdateDeliveryMethod}
          />
        ))}
      </ul>

      <footer className={css.footer}>
        {!hasLiveRows ? (
          <p className={css.allDeadNotice}>
            <FormattedMessage id="CartBoxCard.nothingLeftToBuy" />
          </p>
        ) : (
          <BoxTotals
            ledger={ledger}
            rows={liveRows}
            intl={intl}
            isPickup={isPickup}
            packingClass={packingClass}
          />
        )}

        {hasLiveRows && doaPolicy ? (
          <p className={css.policyBlock}>
            <span className={css.policyLabel}>
              <FormattedMessage id="CartBoxCard.doaPolicyLabel" />
            </span>{' '}
            <span className={css.policyBody}>{doaPolicy}</span>
          </p>
        ) : null}

        {hasLiveRows ? (
          checkoutControl
        ) : (
          <PrimaryButton className={css.checkoutButton} type="button" disabled>
            <FormattedMessage id="CartBoxCard.checkoutThisBox" />
          </PrimaryButton>
        )}

        {hasLiveRows && sellerBoxCount > 1 && sellerName ? (
          <p className={css.paysNote}>
            {otherBoxNumber ? (
              <FormattedMessage
                id="CartBoxCard.otherBoxStays"
                values={{ sellerName, otherBox: otherBoxNumber }}
              />
            ) : (
              <FormattedMessage id="CartBoxCard.otherBoxesStay" values={{ sellerName }} />
            )}
          </p>
        ) : null}
      </footer>
    </section>
  );
};

export default CartBoxCard;
